import { Box, Caption, CaptionBold, LabelBold, Small, SmallBold, Tiny, VerticalCard } from '@comeon/ui';
import { FC, Fragment, MouseEventHandler, PropsWithChildren, ReactNode } from 'react';
import LazyLoadImage from '../../utilities/lazyload-image';
import { Item } from '~/components/item-detail-view/item-detail-view-types';
import styled from 'styled-components';
import Icon, { ICON_DIAMOND_LINE, ICON_INFO } from '~/components/icon';
import { Link } from '~/utils/navigation';
import type { EnhancedAndFormattedImageView } from '~/components/casino-shared/utilities/enhanceImage';

const SIZES = '240px';

interface Props {
	item: Item;
	image?: EnhancedAndFormattedImageView;
	title?: string;
	description?: string;
	fullDescription?: string;
	info?: string;
	type?: string;
	onClick?: MouseEventHandler;
	dataAt?: string;
	href?: string;
	to?: string;
	alt?: string;
	linkClassName?: string;
	width?: string | number;
	height?: string | number;
	imageSize?: string;
	dataTestId?: string;
	buttonLink?: boolean;
	cardWrap?: boolean;
	cardTopRightIcon?: ReactNode;
	cardMediaChipIcon?: ReactNode;
	cardBottomContent?: ReactNode;
	cardMediaChipText?: string;
	cardMediaChipTextDataAt?: string;
	cardMediaChipIconDataAt?: string;
	clampHeader?: boolean;
}

interface WrapperProps {
	href?: string;
	to?: string;
	dataAt?: string;
	onClick?: MouseEventHandler;
	width?: string | number;
	height?: string | number;
	linkClassName?: string;
	buttonLink?: boolean;
}

interface BodyProps {
	dataAt?: string;
	cardHeaderType?: string;
	cardHeaderTitle?: string;
	cardDescription?: string;
	cardFullDescription?: string;
	cardPoints?: number;
	cardOldPoints?: number;
	clampHeader?: boolean;
}

interface StyledIconProps {
	color?: string;
	float?: string;
	margin?: string;
}

interface StyledLabelBoldProps {
	color?: string;
	float?: string;
	clampHeader?: boolean;
}

const StyledBox = styled(Box)`
	cursor: pointer;
`;

const StyledSmall = styled(Small)`
	white-space: nowrap;
	color: var(--main-text-light);
`;

const StyledSmallBold = styled(SmallBold)`
	white-space: nowrap;
	color: var(--main-on-badge);
`;

const StyledType = styled(Caption)`
	color: var(--main-text-supportive);
`;

const StyledInfoBg = styled(Box)`
	backdrop-filter: blur(10px);
`;

const DropShadowBox = styled(Box)`
	filter: drop-shadow(0 4px 16px rgba(0, 0, 0, 0.25));
`;

const StyledSaleText = styled(Caption)`
	color: var(--main-text-supportive);
	float: left;
	text-decoration: line-through;
	margin-top: -3px;
`;

const StyledCaption = styled(Caption)`
	-webkit-line-clamp: 3;
	display: -webkit-box;
	-webkit-box-orient: vertical;
	word-break: break-word;
	text-overflow: ellipsis;
	overflow: hidden;
	text-wrap: balance;
`;

const StyledPointsIcon = styled(Icon)<StyledIconProps>`
	color: ${props => props.color};
	float: ${props => props.float};
	margin: ${props => props.margin};
`;

const StyledPointsText = styled(CaptionBold)<StyledLabelBoldProps>`
	color: ${props => props.color};
	float: ${props => props.float};
	margin-top: -3px;
`;

const StyledLabelBold = styled(LabelBold)<StyledLabelBoldProps>`
	color: ${props => props.color};
	float: ${props => props.float};
	-webkit-line-clamp: ${props => (props.clampHeader ? '2' : '3')};
	display: -webkit-box;
	-webkit-box-orient: vertical;
	word-break: break-word;
	-webkit-hyphens: auto;
	text-overflow: ellipsis;
	overflow: hidden;
	text-wrap: balance;
`;

const BodyContent: FC<BodyProps> = ({
	dataAt,
	cardHeaderType,
	cardHeaderTitle,
	cardDescription,
	cardFullDescription,
	cardPoints,
	cardOldPoints,
	clampHeader
}) => {
	return (
		<Box display="flex" flexDirection="column" justifyContent="center" height="100%" pr={[0, 0, 0, 5]}>
			<Box height="100%" flexDirection="column" justifyContent="space-between" overflow="hidden" pr={5}>
				{cardHeaderType && <StyledType mb={3}>{cardHeaderType}</StyledType>}
				<StyledLabelBold clampHeader={clampHeader} color="var(--main-text)">
					{cardHeaderTitle}
				</StyledLabelBold>
				{cardFullDescription ? (
					<Box mt={2} mb={cardPoints ? '44px' : '5px'}>
						<Tiny>{cardFullDescription}</Tiny>
					</Box>
				) : (
					<StyledCaption mb={cardPoints ? '38px' : '5px'}>{cardDescription}</StyledCaption>
				)}
				{cardPoints && (
					<Box position="absolute" bottom="12px">
						{cardOldPoints && (
							<Box
								bg="var(--generic-stroke-10-percent)"
								borderRadius="14px"
								paddingX="8px"
								paddingY="4px"
								display="inline-block"
								marginRight="6px"
							>
								<StyledSaleText>{cardOldPoints}</StyledSaleText>
								<StyledPointsIcon
									float="right"
									color="var(--main-text-supportive)"
									width={12}
									height={12}
									type={ICON_DIAMOND_LINE}
								/>
							</Box>
						)}
						<Box
							bg="var(--main-primary)"
							borderRadius="14px"
							paddingX="8px"
							paddingY="4px"
							display="inline-block"
							data-at={dataAt && `${dataAt}-points`}
						>
							<StyledPointsText marginTop="1px" float="left" color="var(--main-on-primary)">
								{cardPoints}
							</StyledPointsText>
							<StyledPointsIcon
								float="right"
								color="var(--main-on-primary)"
								width={12}
								height={12}
								type={ICON_DIAMOND_LINE}
							/>
						</Box>
					</Box>
				)}
			</Box>
		</Box>
	);
};

const Wrapper: FC<PropsWithChildren<WrapperProps>> = ({
	children,
	href,
	to,
	dataAt,
	width,
	height,
	onClick,
	linkClassName,
	buttonLink
}) => {
	if (href && !buttonLink) {
		return (
			<StyledBox data-at={dataAt} width={width} height={height}>
				<a
					href={href}
					className={linkClassName}
					target="_blank"
					rel="noopener noreferrer"
					onClick={onClick}
					style={{ height: '100%', width: '100%' }}
				>
					{children}
				</a>
			</StyledBox>
		);
	}
	if (to && !buttonLink) {
		return (
			<StyledBox data-at={dataAt} width={width} height={height}>
				<Link href={to} className={linkClassName} onClick={onClick} style={{ height: '100%', width: '100%' }}>
					{children}
				</Link>
			</StyledBox>
		);
	}

	return (
		<StyledBox data-at={dataAt} onClick={onClick} width={width} height={height}>
			{children}
		</StyledBox>
	);
};

const MarketplaceCard: FC<Props> = ({
	item,
	image,
	title,
	type,
	description,
	fullDescription,
	info,
	onClick,
	href,
	to,
	alt,
	linkClassName,
	width,
	height,
	imageSize,
	dataTestId,
	dataAt,
	buttonLink,
	cardBottomContent,
	cardWrap,
	cardTopRightIcon,
	cardMediaChipIcon,
	cardMediaChipText,
	cardMediaChipIconDataAt,
	cardMediaChipTextDataAt,
	clampHeader
}) => {
	return (
		<Wrapper
			onClick={onClick}
			href={href}
			to={to}
			dataAt={dataAt}
			width={width}
			height={height}
			buttonLink={buttonLink}
			linkClassName={linkClassName}
		>
			<VerticalCard
				biggerBorderRadius
				cardHeaderTitle=""
				dataTestId={dataTestId}
				cardMediaImageComponent={
					<Box p="5" display="inline-flex" overflow="hidden" className="shop-item-img">
						{item.imageView?.src && (
							<Fragment>
								{item.oldPlayerPointsCost && item.playerPointsCost && (
									<DropShadowBox
										bg="var(--main-badge)"
										borderRadius="20px"
										paddingX="8px"
										paddingY="3px"
										display="inline-block"
										position="absolute"
										top={7}
										left={7}
									>
										<StyledSmallBold>
											-
											{Math.round(
												((item.oldPlayerPointsCost - item.playerPointsCost) /
													item.oldPlayerPointsCost) *
													100
											)}
											%
										</StyledSmallBold>
									</DropShadowBox>
								)}
								{info && (
									<StyledInfoBg
										bg="rgba(0, 0, 0, 0.3)"
										margin="8px"
										position="absolute"
										bottom="12px"
										borderRadius="12px"
										paddingX="6px"
										paddingY="2px"
									>
										<StyledSmall>
											<StyledPointsIcon float="left" width={14} height={14} type={ICON_INFO} />
											{info}
										</StyledSmall>
									</StyledInfoBg>
								)}
								{image && (
									<LazyLoadImage
										sizes={SIZES}
										lazyload
										src={image.src}
										srcSet={image.srcSet}
										alt={alt || item.header || ''}
										pictureElementData={image.formats}
										style={{
											aspectRatio: '1/1',
											borderRadius: '6px',
											height: imageSize || '214px',
											width: imageSize || '214px'
										}}
									/>
								)}
							</Fragment>
						)}
					</Box>
				}
				cardBodyContent={
					<BodyContent
						dataAt={dataAt}
						cardHeaderType={type}
						cardHeaderTitle={title}
						cardDescription={description}
						cardFullDescription={fullDescription}
						cardPoints={item.playerPointsCost}
						cardOldPoints={item.oldPlayerPointsCost}
						clampHeader={clampHeader}
					/>
				}
				cardBottomContent={cardBottomContent}
				cardWrap={cardWrap}
				cardTopRightIcon={cardTopRightIcon}
				cardMediaChipText={cardMediaChipText}
				cardMediaChipIcon={cardMediaChipIcon}
				cardMediaChipTextDataAt={cardMediaChipTextDataAt}
				cardMediaChipIconDataAt={cardMediaChipIconDataAt}
			/>
		</Wrapper>
	);
};

export default MarketplaceCard;
